"use client";

import { useFoodCreatorAuth } from "@/lib/food-creator-auth";
import { usePathname, useRouter, useSearchParams } from "next/navigation";
import { type ReactNode, useEffect, useMemo } from "react";

type FoodCreatorAuthGuardMode = "dashboard" | "onboarding";

type FoodCreatorAuthGuardProps = {
  mode: FoodCreatorAuthGuardMode;
  children: ReactNode;
};

function normalizeInternalReturnTo(rawReturnTo?: string | null): string | null {
  if (!rawReturnTo) return null;
  if (!rawReturnTo.startsWith("/")) return null;
  if (rawReturnTo.startsWith("//")) return null;
  if (!rawReturnTo.startsWith("/food-creator")) return null;
  return rawReturnTo;
}

function buildSignInUrl(returnTo: string): string {
  const params = new URLSearchParams();
  params.set("notDismissable", "true");
  params.set("returnTo", returnTo);
  return `/food-creator/sign-in?${params.toString()}`;
}

export function FoodCreatorLoadingScreen({ label }: { label: string }) {
  return (
    <div className="min-h-screen bg-[#FAFCF8] px-6 py-10">
      <div className="mx-auto flex min-h-[70vh] w-full max-w-3xl items-center justify-center">
        <div className="w-full rounded-[28px] bg-[#FFF8F1] px-8 py-10 text-center">
          <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-white">
            <div className="h-8 w-8 animate-spin rounded-full border-[3px] border-[#094327]/20 border-t-[#094327]" />
          </div>
          <p className="text-2xl leading-none text-[#031D11]">Preparing your workspace</p>
          <p className="mt-3 text-sm text-[#5C6B61]">{label}</p>
        </div>
      </div>
    </div>
  );
}

export function FoodCreatorAuthGuard({
  mode,
  children,
}: FoodCreatorAuthGuardProps) {
  const {
    foodCreator,
    isAuthenticated,
    isLoading,
    isBasicOnboardingComplete,
    isOnboardingComplete,
  } = useFoodCreatorAuth();
  const router = useRouter();
  const pathname = usePathname();
  const searchParams = useSearchParams();

  const currentPath = useMemo(() => {
    const queryString = searchParams?.toString();
    return queryString ? `${pathname}?${queryString}` : pathname;
  }, [pathname, searchParams]);

  const signInUrl = useMemo(
    () =>
      buildSignInUrl(
        normalizeInternalReturnTo(currentPath) || "/food-creator/dashboard",
      ),
    [currentPath],
  );

  const isProfileRoute = pathname === "/food-creator/profile";
  const isPersistentOnboardingRoute =
    pathname?.startsWith("/food-creator/onboarding/documents") ?? false;
  const hasSkippedTraining = foodCreator?.complianceTrainingSkipped === true;
  const needsBasicOnboarding = !foodCreator || !isBasicOnboardingComplete;
  const needsCreatorApplication = !foodCreator;
  const needsComplianceTraining =
    !!foodCreator &&
    isBasicOnboardingComplete &&
    !isOnboardingComplete &&
    !hasSkippedTraining;

  useEffect(() => {
    if (isLoading) return;

    // Use the resilient auth state here so transient session revalidation
    // failures do not bounce the user between protected routes and sign-in.
    if (!isAuthenticated) {
      router.replace(signInUrl);
      return;
    }

    if (mode === "dashboard") {
      if (needsCreatorApplication) {
        router.replace("/become-a-food-creator/apply");
        return;
      }

      if (needsBasicOnboarding && !isProfileRoute) {
        router.replace("/food-creator/profile");
        return;
      }

      if (needsComplianceTraining) {
        router.replace("/food-creator/onboarding/course");
      }
      return;
    }

    // mode === "onboarding"
    if (needsCreatorApplication) {
      router.replace("/become-a-food-creator/apply");
      return;
    }

    if (needsBasicOnboarding) {
      router.replace("/food-creator/profile");
      return;
    }

    if (!needsComplianceTraining && !isPersistentOnboardingRoute) {
      router.replace("/food-creator/dashboard");
    }
  }, [
    isAuthenticated,
    isLoading,
    isPersistentOnboardingRoute,
    needsCreatorApplication,
    isProfileRoute,
    mode,
    needsBasicOnboarding,
    needsComplianceTraining,
    router,
    signInUrl,
  ]);

  if (isLoading) {
    return <FoodCreatorLoadingScreen label="Loading..." />;
  }

  if (!isAuthenticated) {
    return <FoodCreatorLoadingScreen label="Redirecting to sign in..." />;
  }

  if (mode === "dashboard") {
    if (
      needsCreatorApplication ||
      (needsBasicOnboarding && !isProfileRoute) ||
      needsComplianceTraining
    ) {
      return <FoodCreatorLoadingScreen label="Loading..." />;
    }
    return <>{children}</>;
  }

  // mode === "onboarding"
  if (
    needsCreatorApplication ||
    needsBasicOnboarding ||
    (!needsComplianceTraining && !isPersistentOnboardingRoute)
  ) {
    return <FoodCreatorLoadingScreen label="Loading..." />;
  }

  return <>{children}</>;
}
